import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import injectSheet from 'react-jss';
import round from '../helpers/round';
import defaultImage from '../img/spacer';
import { imageType } from '../propTypes';
import get from '../helpers/get';
import { blurFilterIsFastEnough } from '../helpers/supportsBlurEffect';

class ResponsiveImage extends Component {
  shouldComponentUpdate(nextProps) {
    // only change if the images change
    const { images, className, classes } = this.props;

    return get(images, [0, 'url']) !== get(nextProps.images, [0, 'url'])
            || className !== nextProps.className
            || classes !== nextProps.classes;
  }

  // only apply height if aspect is not 2
  applyHeight = aspect => !(aspect === 2);

  hideOnLazyLoaded = el => {
    if (el) {
      const blurImg = el;
      blurImg.parentNode.addEventListener('lazyloaded', () => {
        blurImg.style.opacity = 0;
      });
    }
  };

  renderBlur() {
    const {
      alt, blur, classes, aspect,
    } = this.props;
    const applyHeight = this.applyHeight(aspect);

    if (blur) {
      return (
        <img
          ref={this.hideOnLazyLoaded}
          key="blur"
          src={blur.data || blur.url}
          className={cn(classes.imageBlur, { applyHeight })}
          alt={alt}
        />
      );
    }
    return null;
  }

  renderPicture() {
    const {
      alt, classes, images, aspect,
    } = this.props;
    const hasWebp = !!get(images, [0, 'webpUrl']);
    const srcset = key => images.map(item => (`${item[key]} ${item.width}w`)).join();
    const applyHeight = this.applyHeight(aspect);

    return (
      <picture>
        {images && <source data-srcset={srcset('url')} />}
        {hasWebp && <source data-srcset={srcset('webpUrl')} type="image/webp" />}
        <img
          data-sizes="auto"
          alt={alt}
          className={cn(images && 'lazyload', classes.image, { applyHeight })}
          src={defaultImage}
        />
      </picture>
    );
  }

  render() {
    const {
      aspect, children, classes, className,
    } = this.props;
    return (
      <div className={cn(classes.container, aspect && classes.fixedAspect, className)}>
        {this.renderPicture()}
        {this.renderBlur()}
        {children}
      </div>
    );
  }
}

ResponsiveImage.propTypes = {
  alt: PropTypes.string,
  aspect: PropTypes.number,
  blur: imageType,
  children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  images: PropTypes.arrayOf(imageType),
};

ResponsiveImage.defaultProps = {
  alt: undefined,
  aspect: undefined,
  blur: undefined,
  children: undefined,
  className: undefined,
  images: undefined,
};

const BLUR_PIXELS = 10;

export default injectSheet({
  container: {
    position: 'relative',
    width: '100%',
    display: 'block',
    overflow: 'hidden',
    marginLeft: 'auto',
    marginRight: 'auto',
    '&::before': {
      display: 'block',
      content: '""',
    },
  },
  fixedAspect: {
    '&::before': {
      paddingTop: ({ aspect = 1 }) => `${round(100 / aspect)}%`,
    },
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    '&.applyHeight': {
      height: '100%',
    },
    transition: 'all 0.3s',
    objectFit: 'cover',
    fontFamily: '"object-fit: cover;"', // object-fit polyfill
  },
  imageBlur: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    '&.applyHeight': {
      height: '100%',
    },
    filter: blurFilterIsFastEnough ? `blur(${BLUR_PIXELS}px)` : 'none',
    objectFit: 'cover',
    fontFamily: '"object-fit: cover;"', // object-fit polyfill
    transform: 'scale(1.1)',
    transition: 'opacity 400ms',
  },
  link: {
    textDecoration: 'none',
  },
})(ResponsiveImage);
